import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Avatar,
  Button,
  Combobox,
  FormField,
  Select,
  SelectItem,
} from '@ntconsult/aluma-react';
import { useUsers } from '@features/users/hooks/useUsers';
import { useDebouncedValue } from '@common/hooks/useDebouncedValue';
import type { SharePermission } from '../../types';

interface AddPersonFormProps {
  excludeUserIds: string[];
  pending: boolean;
  onCancel: () => void;
  onGrant: (userId: string, permission: SharePermission) => void;
}

export function AddPersonForm({
  excludeUserIds,
  pending,
  onCancel,
  onGrant,
}: AddPersonFormProps) {
  const { t } = useTranslation(['shares', 'common']);
  const [search, setSearch] = useState('');
  const debounced = useDebouncedValue(search, 300);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [level, setLevel] = useState<SharePermission>('read');

  const { data } = useUsers({ search: debounced });
  const availableUsers = (data?.data ?? []).filter(
    (u) => !excludeUserIds.includes(u.id),
  );
  const selectedUser = availableUsers.find((u) => u.id === selectedUserId);

  const submit = () => {
    if (!selectedUserId) return;
    onGrant(selectedUserId, level);
  };

  return (
    <div className="bg-muted rounded-lg p-4 mb-3 flex flex-col gap-3">
      <FormField label={t('people.searchLabel')}>
        <Combobox
          value={selectedUserId}
          onValueChange={setSelectedUserId}
          placeholder={t('people.searchPlaceholder')}
          filter={() => true}
        >
          <Combobox.Trigger
            renderValue={() =>
              selectedUser ? (
                <span className="inline-flex items-center gap-2 min-w-0">
                  <Avatar name={selectedUser.name} size="xs" />
                  <span className="truncate">{selectedUser.name}</span>
                </span>
              ) : (
                t('people.searchPlaceholder')
              )
            }
          />
          <Combobox.Content withSearchInput={false}>
            <Combobox.Input
              placeholder={t('people.searchPlaceholder')}
              onInput={(e) =>
                setSearch((e.target as HTMLInputElement).value)
              }
            />
            <Combobox.List>
              <Combobox.Empty>{t('people.noResults')}</Combobox.Empty>
              {availableUsers.map((user) => (
                <Combobox.Option
                  key={user.id}
                  value={user.id}
                  keywords={[user.name, user.email]}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <Avatar name={user.name} size="sm" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">
                        {user.name}
                      </p>
                      <p className="text-xs text-foreground-muted truncate">
                        {user.email}
                      </p>
                    </div>
                  </div>
                </Combobox.Option>
              ))}
            </Combobox.List>
          </Combobox.Content>
        </Combobox>
      </FormField>

      <div className="flex items-end gap-2">
        <FormField label={t('people.level.label')} className="flex-1">
          <Select
            value={level}
            onValueChange={(v) => setLevel(v as SharePermission)}
          >
            <SelectItem value="read">{t('people.level.read')}</SelectItem>
            <SelectItem value="write">{t('people.level.write')}</SelectItem>
          </Select>
        </FormField>
        <Button
          variant="ghost"
          size="sm"
          onClick={onCancel}
          disabled={pending}
        >
          {t('common:actions.cancel')}
        </Button>
        <Button
          size="sm"
          onClick={submit}
          disabled={!selectedUserId}
          loading={pending}
        >
          {t('people.grant')}
        </Button>
      </div>
    </div>
  );
}
